import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldAlert, TriangleAlert } from "lucide-react";
import {
  fetchSocialReports,
  dismissPostReports,
  adminDeletePost,
  type AdminReportedPost,
} from "../../api/admin";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";

function formatDate(value: string | null | undefined) {
  return value ? new Date(value).toLocaleString() : "—";
}

function excerpt(body: string | null, max = 140): string {
  if (!body) return "—";
  return body.length > max ? `${body.slice(0, max)}…` : body;
}

function ReportRow({ report }: { report: AdminReportedPost }) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin/social/reports"] });

  const dismissMutation = useMutation({
    mutationFn: () => dismissPostReports(report.postId),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: () => adminDeletePost(report.postId),
    onSuccess: invalidate,
  });

  const removed = !!report.post.deletedAt;

  function handleRemove() {
    if (window.confirm("Remove this post? This soft-deletes it and its comments.")) {
      deleteMutation.mutate();
    }
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0,1.4fr) 80px minmax(0,2fr) 70px minmax(0,1.4fr) 130px 90px 220px",
        gap: 12,
        alignItems: "center",
        padding: "10px 0",
        borderBottom: "1px solid var(--border-subtle)",
        minWidth: 1000,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {report.post.authorUsername ? `@${report.post.authorUsername}` : "—"}
        </div>
        <div style={{ fontSize: 11.5, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {report.post.authorEmail}
        </div>
      </div>
      <span style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>{report.post.kind}</span>
      <span style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.4 }}>{excerpt(report.post.body)}</span>
      <span className="tnum" style={{ fontSize: 13, fontWeight: 700, color: "var(--coral-300)" }}>
        {report.reportCount}
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {report.reasons.slice(0, 3).map((r, i) => (
          <span
            key={i}
            style={{ fontSize: 11.5, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {r}
          </span>
        ))}
        {report.reasons.length === 0 && <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>—</span>}
      </div>
      <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)" }}>{formatDate(report.latestReportAt)}</span>
      <span>
        {removed ? <Badge variant="neutral">Removed</Badge> : <Badge variant="success">Active</Badge>}
      </span>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Button variant="ghost" size="sm" disabled={dismissMutation.isPending} onClick={() => dismissMutation.mutate()}>
          {dismissMutation.isPending ? "Dismissing…" : "Dismiss"}
        </Button>
        {!removed && (
          <Button variant="danger" size="sm" disabled={deleteMutation.isPending} onClick={handleRemove}>
            {deleteMutation.isPending ? "Removing…" : "Remove post"}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function AdminSocialPage() {
  const { user } = useAuth();

  const reportsQuery = useQuery({
    queryKey: ["admin/social/reports"],
    queryFn: fetchSocialReports,
    enabled: !!user?.isAdmin,
  });

  if (!user?.isAdmin) return <AccessDenied />;

  const reports = reportsQuery.data?.reports ?? [];

  const headerCell: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  return (
    <AdminShell
      title="Social"
      action={
        reports.length > 0 ? (
          <Badge variant="danger" icon={<ShieldAlert size={11} />}>{reports.length} open</Badge>
        ) : undefined
      }
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-sm), var(--ring-top)",
          padding: "6px 18px 12px",
          overflowX: "auto",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0,1.4fr) 80px minmax(0,2fr) 70px minmax(0,1.4fr) 130px 90px 220px",
            gap: 12,
            padding: "10px 0 8px",
            borderBottom: "1px solid var(--border-subtle)",
            minWidth: 1000,
          }}
        >
          <span style={headerCell}>Author</span>
          <span style={headerCell}>Kind</span>
          <span style={headerCell}>Body</span>
          <span style={headerCell}>Reports</span>
          <span style={headerCell}>Reasons</span>
          <span style={headerCell}>Latest report</span>
          <span style={headerCell}>Status</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Actions</span>
        </div>

        {reportsQuery.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 0" }}>
            <Skel height={30} />
            <Skel height={30} />
            <Skel height={30} />
          </div>
        ) : reports.length === 0 ? (
          <EmptyState
            icon={<TriangleAlert size={20} />}
            title="No open reports"
            hint="Reported posts will show up here for review."
            style={{ margin: "12px 0" }}
          />
        ) : (
          reports.map((r) => <ReportRow key={r.postId} report={r} />)
        )}
      </div>
    </AdminShell>
  );
}
